import React, { useEffect, useRef, useState } from "react";
import { useViewport } from "react-flow-renderer";
import "./MagicCursor.css";

// Flow-space card footprint used to compute grab/drop anchor points.
export const CARD_W = 200;
export const CARD_H = 300;

const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/**
 * Animated "magic cursor" that picks cards up from the deck and carries them
 * to their Design-Thinking zone, one after another, with a smooth arc.
 *
 * `moves` is an array of { nodeId, label, group, image, fromFlow, toFlow } in
 * React Flow (canvas) coordinates. The component lives inside the provider so
 * its own useViewport() always reflects the live pan/zoom.
 */
export default function MagicCursor({ moves, onCommit, onComplete }) {
  const { x: vx, y: vy, zoom } = useViewport();
  const [cursor, setCursor] = useState(null); // {x,y} flow coords
  const [ghost, setGhost] = useState(null); // {x,y,image,label}
  const [phase, setPhase] = useState("idle");
  const raf = useRef(0);
  const timers = useRef([]);

  useEffect(() => {
    if (!moves || moves.length === 0) return undefined;
    let cancelled = false;

    const wait = (ms) =>
      new Promise((res) => timers.current.push(setTimeout(res, ms)));

    const tween = (dur, onStep) =>
      new Promise((res) => {
        const t0 = performance.now();
        const step = (now) => {
          if (cancelled) return;
          const t = Math.min(1, (now - t0) / dur);
          onStep(ease(t));
          if (t < 1) raf.current = requestAnimationFrame(step);
          else res();
        };
        raf.current = requestAnimationFrame(step);
      });

    (async () => {
      const firstAnchor = moves[0].fromFlow || moves[0].toFlow;
      let prev = {
        x: firstAnchor.x - 200,
        y: firstAnchor.y - 140,
      };
      setCursor(prev);

      for (let i = 0; i < moves.length; i += 1) {
        if (cancelled) return;
        const m = moves[i];
        const b = m.toFlow;

        if (m.kind === "note") {
          // A fresh mentor-authored note has no card to pick up from — no
          // deck origin exists. Materialize directly above the target slot
          // and ease down into place; no grab/carry phases.
          const above = { x: b.x, y: b.y - 60 };
          setPhase("move");
          setGhost(null);
          const start = prev;
          await tween(300, (e) =>
            setCursor({
              x: start.x + (above.x - start.x) * e,
              y: start.y + (above.y - start.y) * e,
            })
          );
          if (cancelled) return;

          setPhase("materialize");
          setGhost({ x: above.x, y: above.y, isNote: true, text: m.text, scale: 0 });
          await wait(80);
          if (cancelled) return;

          setPhase("dropin");
          await tween(320, (e) => {
            setGhost((g) =>
              g ? { ...g, y: above.y + (b.y - above.y) * e, scale: Math.min(1, e * 1.2) } : g
            );
          });
          if (cancelled) return;

          setPhase("drop");
          if (onCommit) onCommit(m);
          setGhost(null);
          prev = b;
          setCursor(b);
          continue;
        }

        const a = m.fromFlow;
        const grab = { x: a.x + CARD_W * 0.5, y: a.y + CARD_H * 0.3 };
        const drop = { x: b.x + CARD_W * 0.5, y: b.y + CARD_H * 0.3 };

        // 1. fly the cursor over to the card sitting in the deck
        setPhase("move");
        setGhost(null);
        const start = prev;
        await tween(620, (e) =>
          setCursor({
            x: start.x + (grab.x - start.x) * e,
            y: start.y + (grab.y - start.y) * e,
          })
        );
        if (cancelled) return;

        // 2. grab — the ghost card pops up under the cursor
        setPhase("grab");
        setGhost({ x: a.x, y: a.y, image: m.image, label: m.label });
        await wait(240);
        if (cancelled) return;

        // 3. carry the card across the board on a gentle arc
        setPhase("carry");
        await tween(840, (e) => {
          const lift = Math.sin(Math.PI * e) * 38;
          setCursor({
            x: grab.x + (drop.x - grab.x) * e,
            y: grab.y + (drop.y - grab.y) * e - lift,
          });
          setGhost((g) =>
            g
              ? {
                  ...g,
                  x: a.x + (b.x - a.x) * e,
                  y: a.y + (b.y - a.y) * e - lift,
                }
              : g
          );
        });
        if (cancelled) return;

        // 4. drop it into the zone and commit the real node position
        setPhase("drop");
        if (onCommit) onCommit(m);
        setGhost((g) => (g ? { ...g, x: b.x, y: b.y } : g));
        await wait(280);
        setGhost(null);
        prev = drop;
        setCursor(drop);
      }

      if (cancelled) return;
      setPhase("idle");
      setCursor(null);
      setGhost(null);
      if (onComplete) onComplete();
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf.current);
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moves]);

  if (phase === "idle" && !cursor && !ghost) return null;

  const toPx = (fx) => fx * zoom + vx;
  const toPy = (fy) => fy * zoom + vy;
  const w = CARD_W * zoom;
  const h = CARD_H * zoom;
  const NOTE_W = 200;
  const NOTE_H = 140;

  return (
    <div className="magic-layer">
      {ghost && ghost.isNote && (
        <div
          className="magic-ghost is-note"
          style={{
            left: toPx(ghost.x),
            top: toPy(ghost.y),
            width: NOTE_W * zoom,
            height: NOTE_H * zoom,
            transform: `scale(${ghost.scale ?? 1})`,
          }}
        >
          <div className="magic-ghost__note">{ghost.text}</div>
        </div>
      )}
      {ghost && !ghost.isNote && (
        <div
          className={`magic-ghost ${phase === "carry" ? "is-flying" : ""} ${
            phase === "drop" ? "is-drop" : ""
          }`}
          style={{ left: toPx(ghost.x), top: toPy(ghost.y), width: w, height: h }}
        >
          <img src={ghost.image} alt={ghost.label} draggable="false" />
        </div>
      )}
      {cursor && (
        <div
          className={`magic-cursor is-${phase}`}
          style={{ left: toPx(cursor.x), top: toPy(cursor.y) }}
        >
          <span className="magic-glow" />
          <svg viewBox="0 0 24 24" className="magic-pointer" aria-hidden="true">
            <path d="M4 2l15.5 7.7-6.8 1.8L9.6 19.5z" />
          </svg>
          <span className="magic-spark sp1" />
          <span className="magic-spark sp2" />
          <span className="magic-spark sp3" />
        </div>
      )}
    </div>
  );
}
